'use client';

import { FEATURE_FLAG_KEYS } from 'shop-ui/constants';
import { combineComponent } from 'shop-ui/tools/combineComponent';
import { GRID_LAYOUT_TYPE } from 'shop-ui/section/Grid/rules';
import { OFFICIAL_FONT_SIZE_KEY, createRichText } from 'shop-ui/component/RichText';
import { createButton, OFFICIAL_BUTTON_STYLE_KEY } from 'shop-ui/component/Button';
import { createMedia, OFFICIAL_MEDIA_KEY } from 'shop-ui/component/Media';
import i18n from '@shared/i18n';
import { type LayoutNodeStyleMap } from 'app/shop-ui/types';

export enum OFFICIAL_SECTION_EXHIBIT_KEY {
    TEXT = 'introduction-text',
    IMG = 'introduction-img',
    IMG_ARROW = 'introduction-img-arrow',
    TOPIC_RIGHT = 'introduction-topic-right',
    TOPIC_LEFT = 'introduction-topic-left',
    TOPIC_DOUBLE_COLUMN = 'introduction-topic-double-column',
    TOPIC_DOUBLE_COLUMN_FULL_IMG = 'introduction-topic-double-column-full-image',
    TOPIC_HIGHLIGHT = 'introduction-topic-highlight',
    TOPIC_BULLET_LIST = 'introduction-topic-bullet-list',
    TOPIC_CARD_NO_BORDER = 'introduction-topic-card-no-border',
    TOPIC_CARD_WITH_BORDER = 'introduction-topic-card-with-border',
}

export const officialSectionExhibitList = [
    {
        name: OFFICIAL_SECTION_EXHIBIT_KEY.TEXT,
        content: i18n.gettext('Lorem ipsum dolor sit amet consectetur.'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_EXHIBIT_KEY.IMG,
        content: i18n.gettext('Lorem ipsum dolor sit amet consectetur.'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_EXHIBIT_KEY.IMG_ARROW,
        content: i18n.gettext('Lorem ipsum dolor sit amet consectetur.'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_RIGHT,
        content: i18n.gettext('Lorem ipsum dolor'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_LEFT,
        content: i18n.gettext('Lorem ipsum dolor'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_DOUBLE_COLUMN,
        content: i18n.gettext('Lorem ipsum dolor'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_DOUBLE_COLUMN_FULL_IMG,
        content: i18n.gettext('Lorem ipsum dolor sit amet consectetur.'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_HIGHLIGHT,
        content: i18n.gettext('Lorem ipsum dolor'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_BULLET_LIST,
        content: i18n.gettext('Lorem ipsum dolor'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_CARD_NO_BORDER,
        content: i18n.gettext('Lorem ipsum dolor sit amet consectetur.'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_CARD_WITH_BORDER,
        content: i18n.gettext('Lorem ipsum dolor'),
        version: 1,
    },
];

export function createIntroductionText(
    content: string = officialSectionExhibitList[0].content
) {
    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '20',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.8',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '1px',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(32, 32, 38, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
    });

    const paragraph = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        i18n.gettext('Lorem ipsum dolor sit amet consectetur.'),
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(102, 102, 106, 1)',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '64px',
        }
    );

    const primaryButton = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK,
        i18n.gettext('第一按鈕'),
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '52px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '15px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'right',
        }
    );

    const secondaryButton = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_WHITE,
        i18n.gettext('第二按鈕'),
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '52px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '15px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'left',
        }
    );

    return combineComponent(
        i18n.gettext('介紹'),
        [[heading, paragraph], [primaryButton], [secondaryButton]],
        {
            primaryGridType: GRID_LAYOUT_TYPE.SPLIT_V_1C_2C,
            secondaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.GRID_GAP]: '18px',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
                [FEATURE_FLAG_KEYS.PADDING_TOP]: '290px',
                [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '290px',
                [FEATURE_FLAG_KEYS.GRID_ROW_1]: '0.6fr',
                [FEATURE_FLAG_KEYS.GRID_ROW_2]: '0.4fr',
            },
            secondaryGridPresetStyle: [
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'center',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '12px',
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '12px',
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '12px',
                },
            ],
            shouldWrap: true,
        }
    );
}

export function createIntroductionImg(
    content: string = officialSectionExhibitList[1].content,
    presetStyle: {
        heading?: LayoutNodeStyleMap[string];
        paragraph?: LayoutNodeStyleMap[string];
        primaryButton?: LayoutNodeStyleMap[string];
        secondaryButton?: LayoutNodeStyleMap[string];
        primaryGrid?: LayoutNodeStyleMap[string];
    } = {},
    useSecondaryButton: boolean = true
) {
    const {
        heading: headingPresetStyle = {},
        paragraph: paragraphPresetStyle = {},
        primaryButton: primaryButtonPresetStyle = {},
        secondaryButton: secondaryButtonPresetStyle = {},
        primaryGrid: primaryGridPresetStyle = {},
    } = presetStyle;

    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '20',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.8',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '1px',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(32, 32, 38, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        ...headingPresetStyle,
    });

    const paragraph = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        i18n.gettext('Lorem ipsum dolor sit amet consectetur.'),
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(102, 102, 106, 1)',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '64px',
            ...paragraphPresetStyle,
        }
    );

    const primaryButton = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK,
        i18n.gettext('第一按鈕'),
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '52px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '15px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: useSecondaryButton ? 'right' : 'center',
            ...primaryButtonPresetStyle,
        }
    );

    const secondaryButton = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_WHITE,
        i18n.gettext('第二按鈕'),
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '52px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '15px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'left',
            ...secondaryButtonPresetStyle,
        }
    );

    const buttonAreaElement = useSecondaryButton
        ? [[primaryButton], [secondaryButton]]
        : [[primaryButton]];

    const primaryGridType = useSecondaryButton
        ? GRID_LAYOUT_TYPE.SPLIT_V_1C_2C
        : GRID_LAYOUT_TYPE.SPLIT_V_2C;

    return combineComponent(
        i18n.gettext('介紹'),
        [[heading, paragraph], ...buttonAreaElement],
        {
            primaryGridType,
            secondaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.GRID_GAP]: '18px',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: true,
                [FEATURE_FLAG_KEYS.GRID_ROW_1]: '0.6fr',
                [FEATURE_FLAG_KEYS.GRID_ROW_2]: '0.4fr',
                [FEATURE_FLAG_KEYS.BACKGROUND_TYPE]: 'image',
                [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
                    '/static/section-showcut/introduction-img-default-bg.png',
                ...primaryGridPresetStyle,
            },
            secondaryGridPresetStyle: useSecondaryButton
                ? [
                      {
                          [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'end',
                          [FEATURE_FLAG_KEYS.GRID_GAP]: '12px',
                      },
                      {
                          [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                          [FEATURE_FLAG_KEYS.GRID_GAP]: '12px',
                      },
                      {
                          [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                          [FEATURE_FLAG_KEYS.GRID_GAP]: '12px',
                      },
                  ]
                : [
                      {
                          [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'end',
                          [FEATURE_FLAG_KEYS.GRID_GAP]: '12px',
                      },
                      {
                          [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                          [FEATURE_FLAG_KEYS.GRID_GAP]: '12px',
                      },
                  ],
            shouldWrap: true,
        }
    );
}

export function createIntroductionImgArrow(
    content: string = officialSectionExhibitList[2].content
) {
    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, content, {
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '20',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.8',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '1px',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(255, 255, 255, 1)',
        [FEATURE_FLAG_KEYS.PADDING_LEFT]: '4%',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '4%',
    });

    const paragraph = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        i18n.gettext('Lorem ipsum dolor sit amet consectetur.'),
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(222, 222, 222, 1)',
            [FEATURE_FLAG_KEYS.PADDING_LEFT]: '4%',
            [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '4%',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '180px',
        }
    );

    const primaryButton = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK,
        i18n.gettext('View Collection'),
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '0px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '0px',
            [FEATURE_FLAG_KEYS.PADDING_LEFT]: '4%',
            [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '4%',
            [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
            [FEATURE_FLAG_KEYS.BORDER_COLOR]: '',
            [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '0px',
            [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'montserrat',
            [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '400',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(255, 255, 255, 1)',
            [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
            [FEATURE_FLAG_KEYS.ENABLE_LINK]: false,
            [FEATURE_FLAG_KEYS.BUTTON_ICON]: true,
            [FEATURE_FLAG_KEYS.BUTTON_ICON_TYPE]: 'fi-rr-arrow-right',
            [FEATURE_FLAG_KEYS.BUTTON_ICON_POSITION]: 'left',
            [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR]: '',
            [FEATURE_FLAG_KEYS.HOVER_BORDER_COLOR]: '',
            [FEATURE_FLAG_KEYS.HOVER_BORDER_RADIUS]: '',
            [FEATURE_FLAG_KEYS.HOVER_FONT_COLOR]: 'rgba(212, 212, 212, 1)',
            [FEATURE_FLAG_KEYS.HOVER_FONT_STYLE]: '',
            [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR]: '',
            [FEATURE_FLAG_KEYS.ACTIVE_BORDER_COLOR]: '',
            [FEATURE_FLAG_KEYS.ACTIVE_BORDER_RADIUS]: '',
            [FEATURE_FLAG_KEYS.ACTIVE_FONT_COLOR]: 'rgba(200, 200, 200, 1)',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'right',
        }
    );

    return combineComponent(i18n.gettext('介紹'), [[heading, paragraph, primaryButton]], {
        primaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        secondaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        primaryGridPresetStyle: {
            [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
            [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '150px',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '150px',
            [FEATURE_FLAG_KEYS.GRID_GAP]: '18px',
            [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
            [FEATURE_FLAG_KEYS.GRID_ROW_1]: '0.6fr',
            [FEATURE_FLAG_KEYS.GRID_ROW_2]: '0.4fr',
            [FEATURE_FLAG_KEYS.BACKGROUND_TYPE]: 'image',
            [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
                '/static/section-showcut/introduction-img-arrow-default-bg.png',
        },
        secondaryGridPresetStyle: [
            {
                [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'center',
                [FEATURE_FLAG_KEYS.GRID_GAP]: '12px',
            },
        ],
        shouldWrap: true,
    });
}

export function createIntroductionTopicRight(
    content: string = officialSectionExhibitList[3].content,
    presetStyle: {
        heading?: LayoutNodeStyleMap[string];
        paragraph?: LayoutNodeStyleMap[string];
        paragraphContent?: string;
        primaryButton?: LayoutNodeStyleMap[string];
        secondaryButton?: LayoutNodeStyleMap[string];
        primaryGrid?: LayoutNodeStyleMap[string];
        img?: LayoutNodeStyleMap[string];
    } = {},
    useSecondaryButton: boolean = true
) {
    const {
        heading: headingPresetStyle = {},
        paragraph: paragraphPresetStyle = {},
        paragraphContent = i18n.gettext(
            'Lorem Problems trying to resolve the conflict between the two major realms of Classical physics'
        ),
        primaryButton: primaryButtonPresetStyle = {},
        secondaryButton: secondaryButtonPresetStyle = {},
        primaryGrid: primaryGridPresetStyle = {},
        img: imgPresetStyle = {},
    } = presetStyle;

    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.HEADING_SMALL, content, {
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.8',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '1px',
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(0, 0, 0, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '40px',
        ...headingPresetStyle,
    });

    const paragraph = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        paragraphContent,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            ...paragraphPresetStyle,
        }
    );

    const primaryButton = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK,
        i18n.gettext('Button'),
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '31px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '13px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'left',
            ...primaryButtonPresetStyle,
        }
    );

    const secondaryButton = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_WHITE,
        i18n.gettext('Button'),
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '31px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '13px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'left',
            ...secondaryButtonPresetStyle,
        }
    );

    const img = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/introduction-topic-right.png',
        ...imgPresetStyle,
    });

    return combineComponent(
        i18n.gettext('主題介紹 - 右側欄'),
        [
            [img],
            [heading, paragraph],
            [[primaryButton], useSecondaryButton ? [secondaryButton] : []],
        ],
        {
            primaryGridType: GRID_LAYOUT_TYPE.SPLIT_H_1C_2C,
            secondaryGridType: [
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SPLIT_H_2C,
            ],
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.GRID_GAP]: '56px',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
                [FEATURE_FLAG_KEYS.GRID_ROW_1]: '0.55fr',
                [FEATURE_FLAG_KEYS.GRID_ROW_2]: '0.45fr',
                [FEATURE_FLAG_KEYS.GRID_COLUMN_1]: '0.45fr',
                [FEATURE_FLAG_KEYS.GRID_COLUMN_2]: '0.55fr',
                ...primaryGridPresetStyle,
            },
            secondaryGridPresetStyle: [
                {},
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'end',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '8px',
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '24px',
                    [FEATURE_FLAG_KEYS.GRID_COLUMN_1]: '0.3fr',
                    [FEATURE_FLAG_KEYS.GRID_COLUMN_2]: '0.7fr',
                },
            ],
            shouldWrap: true,
        }
    );
}

export function createIntroductionTopicLeft(
    content: string = officialSectionExhibitList[4].content
) {
    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.HEADING_SMALL, content, {
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.8',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '1px',
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(0, 0, 0, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '40px',
    });

    const paragraph = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        i18n.gettext(
            'Lorem Problems trying to resolve the conflict between the two major realms of Classical physics'
        ),
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '40px',
        }
    );

    const primaryButton = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK,
        i18n.gettext('Button'),
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '52px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '15px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'left',
        }
    );

    const secondaryButton = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_WHITE,
        i18n.gettext('Button'),
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '52px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '15px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'left',
        }
    );

    const img = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '0px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/introduction-topic-left.png',
    });

    return combineComponent(
        i18n.gettext('主題介紹 - 左側欄'),
        [[heading, paragraph], [[primaryButton], [secondaryButton]], [img]],
        {
            primaryGridType: GRID_LAYOUT_TYPE.SPLIT_H_2C_1C,
            secondaryGridType: [
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SPLIT_H_2C,
                GRID_LAYOUT_TYPE.SINGLE_CELL,
            ],
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.GRID_GAP]: '56px',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
                [FEATURE_FLAG_KEYS.GRID_ROW_1]: '0.55fr',
                [FEATURE_FLAG_KEYS.GRID_ROW_2]: '0.45fr',
                [FEATURE_FLAG_KEYS.GRID_COLUMN_1]: '0.6fr',
                [FEATURE_FLAG_KEYS.GRID_COLUMN_2]: '0.4fr',
            },
            secondaryGridPresetStyle: [
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'end',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '8px',
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '24px',
                    [FEATURE_FLAG_KEYS.GRID_COLUMN_1]: '0.3fr',
                    [FEATURE_FLAG_KEYS.GRID_COLUMN_2]: '0.7fr',
                },
            ],
            shouldWrap: true,
        }
    );
}

export function createIntroductionTopicDoubleColumn(
    content: string = officialSectionExhibitList[5].content
) {
    const heading1 = createRichText(OFFICIAL_FONT_SIZE_KEY.HEADING_SMALL, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(37, 43, 66, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '700',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
    });

    const heading2 = createRichText(OFFICIAL_FONT_SIZE_KEY.HEADING_SMALL, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(37, 43, 66, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '700',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
    });

    const paragraph1 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        i18n.gettext(
            'Lorem Problems trying to resolve the conflict between the two major realms of Classical physics'
        ),
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '16px',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
        }
    );

    const paragraph2 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        i18n.gettext(
            'Lorem Problems trying to resolve the conflict between the two major realms of Classical physics'
        ),
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '16px',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
        }
    );

    const primaryButton1 = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK,
        i18n.gettext('Button'),
        {
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '16px',
            [FEATURE_FLAG_KEYS.PADDING_X]: '40px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '12px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'right',
        }
    );

    const primaryButton2 = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK,
        i18n.gettext('Button'),
        {
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '16px',
            [FEATURE_FLAG_KEYS.PADDING_X]: '40px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '12px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'right',
        }
    );

    const img1 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '16px',
        [FEATURE_FLAG_KEYS.MEDIA_ASPECT_RATIO]: '112/87',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/topic-introduction-topic-double-column-1_v1.png',
    });

    const img2 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '16px',
        [FEATURE_FLAG_KEYS.MEDIA_ASPECT_RATIO]: '112/87',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/topic-introduction-topic-double-column-2_v1.png',
    });

    return combineComponent(
        i18n.gettext('雙欄式主題介紹'),
        [
            [img1, heading1, paragraph1, primaryButton1],
            [img2, heading2, paragraph2, primaryButton2],
        ],
        {
            primaryGridType: GRID_LAYOUT_TYPE.SPLIT_H_2C,
            secondaryGridType: [
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SINGLE_CELL,
            ],
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.PADDING_TOP]: '64px',
                [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '64px',
                [FEATURE_FLAG_KEYS.GRID_GAP]: '16px',
            },
            secondaryGridPresetStyle: [
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '16px',
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '16px',
                },
            ],
            shouldWrap: true,
        }
    );
}

export function createIntroductionTopicDoubleColumnFullImage(
    content: string = officialSectionExhibitList[6].content,
    presetStyle: {
        heading?: LayoutNodeStyleMap[string];
        heading2?: LayoutNodeStyleMap[string];
        heading2Content?: string;
        heading3?: LayoutNodeStyleMap[string];
        heading3Content?: string;
        button?: LayoutNodeStyleMap[string];
        button2?: LayoutNodeStyleMap[string];
        buttonContent?: string;
        button2Content?: string;
        primaryGrid?: LayoutNodeStyleMap[string];
        secondaryGrid?: LayoutNodeStyleMap[string][];
    } = {}
) {
    const {
        heading: headingPresetStyle,
        heading2: heading2PresetStyle,
        heading3: heading3PresetStyle,
        button: buttonPresetStyle = {},
        button2: button2PresetStyle = {},
        buttonContent = i18n.gettext('我是按鈕'),
        button2Content = i18n.gettext('我是按鈕'),
        primaryGrid: primaryGridPresetStyle = {},
        secondaryGrid: secondaryGridPresetStyle = [],
        heading2Content = officialSectionExhibitList[6].content,
        heading3Content = officialSectionExhibitList[6].content,
    } = presetStyle;

    const heading1 = createRichText(OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(37, 43, 66, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
        [FEATURE_FLAG_KEYS.PADDING_LEFT]: '2%',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '2%',
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '20px',
        [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '20px',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '1px',
        ...headingPresetStyle,
    });

    const heading2 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        heading2Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(255, 255, 255, 1)',
            [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.PADDING_LEFT]: '4%',
            [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '4%',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '30px',
            [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '1px',
            ...heading2PresetStyle,
        }
    );

    const heading3 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        heading3Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(255, 255, 255, 1)',
            [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.PADDING_LEFT]: '4%',
            [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '4%',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '30px',
            [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '1px',
            ...heading3PresetStyle,
        }
    );

    const primaryButton1 = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK,
        buttonContent,
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '40px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '12px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.PADDING_LEFT]: '4%',
            [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '4%',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '28px',
            ...buttonPresetStyle,
        }
    );

    const primaryButton2 = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK,
        button2Content,
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '40px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '12px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.PADDING_LEFT]: '4%',
            [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '4%',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '28px',
            ...button2PresetStyle,
        }
    );

    return combineComponent(
        i18n.gettext('雙欄式主題介紹'),
        [heading1, [heading2, primaryButton1], [heading3, primaryButton2]],
        {
            primaryGridType: GRID_LAYOUT_TYPE.SPLIT_V_1C_2C,
            secondaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.PADDING_TOP]: '24px',
                [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '24px',
                [FEATURE_FLAG_KEYS.GRID_GAP]: '0px',
                [FEATURE_FLAG_KEYS.GRID_COLUMN_1]: '0.5fr',
                [FEATURE_FLAG_KEYS.GRID_COLUMN_2]: '0.5fr',
                [FEATURE_FLAG_KEYS.GRID_ROW_1]: '0.15fr',
                [FEATURE_FLAG_KEYS.GRID_ROW_2]: '0.85fr',
                ...primaryGridPresetStyle,
            },
            secondaryGridPresetStyle: [
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'end',
                    ...(secondaryGridPresetStyle?.[0] || {}),
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'end',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '30px',
                    [FEATURE_FLAG_KEYS.BACKGROUND_TYPE]: 'image',
                    [FEATURE_FLAG_KEYS.MEDIA_ASPECT_RATIO]: '720/643',
                    [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
                        '/static/section-showcut/topic-introduction-highlight-default-bg-1.png',
                    ...(secondaryGridPresetStyle?.[1] || {}),
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'end',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '30px',
                    [FEATURE_FLAG_KEYS.BACKGROUND_TYPE]: 'image',
                    [FEATURE_FLAG_KEYS.MEDIA_ASPECT_RATIO]: '720/643',
                    [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
                        '/static/section-showcut/topic-introduction-highlight-default-bg-2.png',
                    ...(secondaryGridPresetStyle?.[2] || {}),
                },
            ],
            shouldWrap: true,
        }
    );
}

export function createIntroductionHighlight(
    content: string = officialSectionExhibitList[7].content,
    presetStyle: {
        heading?: LayoutNodeStyleMap[string];
        paragraph?: LayoutNodeStyleMap[string];
        heading2?: LayoutNodeStyleMap[string];
        paragraph2?: LayoutNodeStyleMap[string];
        heading3?: LayoutNodeStyleMap[string];
        paragraph3?: LayoutNodeStyleMap[string];
        heading4?: LayoutNodeStyleMap[string];
        paragraph4?: LayoutNodeStyleMap[string];
        heading5?: LayoutNodeStyleMap[string];
        paragraph5?: LayoutNodeStyleMap[string];
        primaryGrid?: LayoutNodeStyleMap[string];
        secondaryGrid?: LayoutNodeStyleMap[string][];
        paragraphContent?: string;
        heading2Content?: string;
        paragraph2Content?: string;
        heading3Content?: string;
        paragraph3Content?: string;
        heading4Content?: string;
        paragraph4Content?: string;
        heading5Content?: string;
        paragraph5Content?: string;
        img?: LayoutNodeStyleMap[string];
    } = {}
) {
    const defaultHeadingContent = i18n.gettext('Lorem ipsum dolor');

    const defaultParagraphContent = i18n.gettext(
        'Lorem Problems trying to resolve the conflict between the two major realms of Classical physics'
    );

    const {
        heading: headingPresetStyle = {},
        paragraph: paragraphPresetStyle = {},
        paragraphContent = defaultParagraphContent,
        heading2: heading2PresetStyle = {},
        paragraph2: paragraph2PresetStyle = {},
        heading2Content = defaultHeadingContent,
        paragraph2Content = defaultParagraphContent,
        heading3: heading3PresetStyle = {},
        paragraph3: paragraph3PresetStyle = {},
        heading3Content = defaultHeadingContent,
        paragraph3Content = defaultParagraphContent,
        heading4: heading4PresetStyle = {},
        paragraph4: paragraph4PresetStyle = {},
        heading4Content = defaultHeadingContent,
        paragraph4Content = defaultParagraphContent,
        heading5: heading5PresetStyle = {},
        paragraph5: paragraph5PresetStyle = {},
        heading5Content = defaultHeadingContent,
        paragraph5Content = defaultParagraphContent,
        primaryGrid: primaryGridPresetStyle = {},
        secondaryGrid: secondaryGridPresetStyle = [],
        img: imgPresetStyle = {},
    } = presetStyle;

    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '18',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(69, 69, 74, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '64px',
        ...headingPresetStyle,
    });

    const paragraph = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        paragraphContent,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '20px',
            ...paragraphPresetStyle,
        }
    );

    const heading2 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        heading2Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(69, 69, 74, 1)',
            [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
            ...heading2PresetStyle,
        }
    );

    const paragraph2 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        paragraph2Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '20px',
            ...paragraph2PresetStyle,
        }
    );

    const heading3 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        heading3Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(69, 69, 74, 1)',
            [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
            ...heading3PresetStyle,
        }
    );

    const paragraph3 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        paragraph3Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '20px',
            ...paragraph3PresetStyle,
        }
    );

    const heading4 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        heading4Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(69, 69, 74, 1)',
            [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
            ...heading4PresetStyle,
        }
    );

    const paragraph4 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        paragraph4Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '20px',
            ...paragraph4PresetStyle,
        }
    );

    const heading5 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        heading5Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(69, 69, 74, 1)',
            [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
            ...heading5PresetStyle,
        }
    );

    const paragraph5 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        paragraph5Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '20px',
            ...paragraph5PresetStyle,
        }
    );

    const img = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/topic-introduction-highlight-default-bg-1_v1.png',
        ...imgPresetStyle,
    });

    return combineComponent(
        i18n.gettext('主題介紹 - 強調'),
        [
            [img],
            [heading, paragraph],
            [
                [heading2, paragraph2],
                [heading3, paragraph3],
            ],
            [
                [heading4, paragraph4],
                [heading5, paragraph5],
            ],
        ],
        {
            primaryGridType: GRID_LAYOUT_TYPE.SPLIT_H_1C_3C,
            secondaryGridType: [
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SPLIT_H_2C,
                GRID_LAYOUT_TYPE.SPLIT_H_2C,
            ],
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
                [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '4%',
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.GRID_GAP]: '70px',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
                [FEATURE_FLAG_KEYS.GRID_ROW_1]: '0.4fr',
                [FEATURE_FLAG_KEYS.GRID_ROW_2]: '0.3fr',
                [FEATURE_FLAG_KEYS.GRID_ROW_3]: '0.3fr',
                [FEATURE_FLAG_KEYS.GRID_COLUMN_1]: '0.45fr',
                [FEATURE_FLAG_KEYS.GRID_COLUMN_2]: '0.55fr',
                ...primaryGridPresetStyle,
            },
            secondaryGridPresetStyle: [
                {
                    ...(secondaryGridPresetStyle?.[0] || {}),
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'center',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '8px',
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                },
            ],
            shouldWrap: true,
        }
    );
}

export function createIntroductionBulletList(
    title: string = officialSectionExhibitList[6].content,
    presetStyle: {
        content?: string;
        buttonStyle?: LayoutNodeStyleMap[string];
        buttonContent?: string;
        title2?: string;
        content2?: string;
        title3?: string;
        content3?: string;
        title4?: string;
        content4?: string;
        title5?: string;
        content5?: string;
        titleStyle?: LayoutNodeStyleMap[string];
        titleStyle2?: LayoutNodeStyleMap[string];
        titleStyle3?: LayoutNodeStyleMap[string];
        titleStyle4?: LayoutNodeStyleMap[string];
        titleStyle5?: LayoutNodeStyleMap[string];
        contentStyle?: LayoutNodeStyleMap[string];
        contentStyle2?: LayoutNodeStyleMap[string];
        contentStyle3?: LayoutNodeStyleMap[string];
        contentStyle4?: LayoutNodeStyleMap[string];
        contentStyle5?: LayoutNodeStyleMap[string];
        title2Style?: LayoutNodeStyleMap[string];
        content2Style?: LayoutNodeStyleMap[string];
        title3Style?: LayoutNodeStyleMap[string];
        content3Style?: LayoutNodeStyleMap[string];
        title4Style?: LayoutNodeStyleMap[string];
        content4Style?: LayoutNodeStyleMap[string];
        title5Style?: LayoutNodeStyleMap[string];
        content5Style?: LayoutNodeStyleMap[string];
        primaryGrid?: LayoutNodeStyleMap[string];
        secondaryGrid?: LayoutNodeStyleMap[string][];
        img1?: LayoutNodeStyleMap[string];
        img2?: LayoutNodeStyleMap[string];
        img3?: LayoutNodeStyleMap[string];
        img4?: LayoutNodeStyleMap[string];
        img5?: LayoutNodeStyleMap[string];
    } = {}
) {
    const defaultTitle = i18n.gettext('Lorem ipsum dolor');

    const defaultContent = i18n.gettext('Lorem ipsum dolor sit amet consectetur.');

    const {
        titleStyle: titleStylePresetStyle,
        content = i18n.gettext(
            'Lorem ipsum dolor sit amet consectetur. Sit sed vitae netus facilisi habitasse odio. Arcu in viverra sit velit. Posuere diam ultricies cras adipiscing consectetur velit leo.'
        ),
        contentStyle: contentStylePresetStyle = {},
        buttonStyle: buttonStylePresetStyle = {},
        buttonContent = i18n.gettext('Lorem ipsum'),
        title2 = defaultTitle,
        title2Style: title2PresetStyle = {},
        content2 = defaultContent,
        content2Style: content2PresetStyle = {},
        title3 = defaultTitle,
        title3Style: title3PresetStyle = {},
        content3 = defaultContent,
        content3Style: content3PresetStyle = {},
        title4 = defaultTitle,
        title4Style: title4PresetStyle = {},
        content4 = defaultContent,
        content4Style: content4PresetStyle = {},
        title5 = defaultTitle,
        title5Style: title5PresetStyle = {},
        content5 = defaultContent,
        content5Style: content5PresetStyle = {},
        img1: img1PresetStyle = {},
        img2: img2PresetStyle = {},
        img3: img3PresetStyle = {},
        img4: img4PresetStyle = {},
        primaryGrid: primaryGridPresetStyle = {},
        secondaryGrid: secondaryGridPresetStyle = [],
    } = presetStyle;

    const title1 = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, title, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '19',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(69, 69, 74, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '35px',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '16%',
        ...titleStylePresetStyle,
    });

    const paragraph1 = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '12',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '35px',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '16%',
        ...contentStylePresetStyle,
    });

    const button = createButton(OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK, buttonContent, {
        [FEATURE_FLAG_KEYS.PADDING_X]: '0px',
        [FEATURE_FLAG_KEYS.PADDING_Y]: '0px',
        [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
        [FEATURE_FLAG_KEYS.BORDER_COLOR]: '',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '0px',
        [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'Inter',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '400',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(81, 81, 86, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
        [FEATURE_FLAG_KEYS.ENABLE_LINK]: false,
        [FEATURE_FLAG_KEYS.BUTTON_ICON]: true,
        [FEATURE_FLAG_KEYS.BUTTON_ICON_TYPE]: 'fi-rr-angle-right',
        [FEATURE_FLAG_KEYS.BUTTON_ICON_POSITION]: 'right',
        [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR]: '',
        [FEATURE_FLAG_KEYS.HOVER_BORDER_COLOR]: '',
        [FEATURE_FLAG_KEYS.HOVER_BORDER_RADIUS]: '',
        [FEATURE_FLAG_KEYS.HOVER_FONT_COLOR]: 'rgba(81, 81, 86, 1)',
        [FEATURE_FLAG_KEYS.HOVER_FONT_STYLE]: '',
        [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR]: '',
        [FEATURE_FLAG_KEYS.ACTIVE_BORDER_COLOR]: '',
        [FEATURE_FLAG_KEYS.ACTIVE_BORDER_RADIUS]: '',
        [FEATURE_FLAG_KEYS.ACTIVE_FONT_COLOR]: 'rgba(81, 81, 86, 1)',
        [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '35px',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '16%',
        ...buttonStylePresetStyle,
    });

    const img1 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '20px',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '80%',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '12px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/topic-introduction-bullet-list-1_v1.png',
        ...img1PresetStyle,
    });

    const _title2 = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, title2, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '14',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(32, 32, 38, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        ...title2PresetStyle,
    });

    const paragraph2 = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, content2, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '12',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
        ...content2PresetStyle,
    });

    //

    const img2 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '20px',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '80%',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '12px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/topic-introduction-bullet-list-2_v1.png',
        ...img2PresetStyle,
    });

    const _title3 = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, title3, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '14',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(32, 32, 38, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        ...title3PresetStyle,
    });

    const paragraph3 = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, content3, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '12',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
        ...content3PresetStyle,
    });

    //

    const img3 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '20px',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '80%',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '12px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/topic-introduction-bullet-list-3_v1.png',
        ...img3PresetStyle,
    });

    const _title4 = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, title4, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '14',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(32, 32, 38, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        ...title4PresetStyle,
    });

    const paragraph4 = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, content4, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '12',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
        ...content4PresetStyle,
    });

    //

    const img4 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '20px',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '80%',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '12px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/topic-introduction-bullet-list-4_v1.png',
        ...img4PresetStyle,
    });

    const _title5 = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, title5, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '14',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(32, 32, 38, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        ...title5PresetStyle,
    });

    const paragraph5 = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, content5, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '12',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
        ...content5PresetStyle,
    });

    return combineComponent(
        i18n.gettext('主題介紹 - 條列式'),
        [
            [title1, paragraph1, button],
            [
                [img1, _title2, paragraph2],
                [img2, _title3, paragraph3],
            ],
            [
                [img3, _title4, paragraph4],
                [img4, _title5, paragraph5],
            ],
        ],
        {
            primaryGridType: GRID_LAYOUT_TYPE.SPLIT_H_3C,
            secondaryGridType: [
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SPLIT_V_2C,
                GRID_LAYOUT_TYPE.SPLIT_V_2C,
            ],
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.GRID_GAP]: '16px',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
                [FEATURE_FLAG_KEYS.GRID_COLUMN_1]: '0.5fr',
                [FEATURE_FLAG_KEYS.GRID_COLUMN_2]: '0.25fr',
                [FEATURE_FLAG_KEYS.GRID_COLUMN_3]: '0.25fr',
                [FEATURE_FLAG_KEYS.PADDING_TOP]: '46px',
                [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '46px',
                ...primaryGridPresetStyle,
            },
            secondaryGridPresetStyle: [
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '16px',
                    ...(secondaryGridPresetStyle[0] || {}),
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'center',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '74px',
                    ...(secondaryGridPresetStyle[1] || {}),
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'center',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '74px',
                    ...(secondaryGridPresetStyle[2] || {}),
                },
            ],
            shouldWrap: true,
        }
    );
}

export function createIntroductionCardNoBorder(
    content: string = officialSectionExhibitList[9].content,
    presetStyle: {
        heading?: LayoutNodeStyleMap[string];
        paragraph?: LayoutNodeStyleMap[string];
        paragraphContent?: string;
        image?: LayoutNodeStyleMap[string];
        heading2?: LayoutNodeStyleMap[string];
        heading2Content?: string;
        paragraph2?: LayoutNodeStyleMap[string];
        paragraph2Content?: string;
        image2?: LayoutNodeStyleMap[string];
        heading3?: LayoutNodeStyleMap[string];
        heading3Content?: string;
        paragraph3?: LayoutNodeStyleMap[string];
        paragraph3Content?: string;
        image3?: LayoutNodeStyleMap[string];
    } = {}
) {
    const {
        heading: headingPresetStyle,
        paragraph: paragraphPresetStyle,
        paragraphContent = i18n.gettext(
            'Lorem ipsum dolor sit amet consectetur. Sit sed vitae netus facilisi habitasse odio. Arcu in viverra sit velit. Posuere diam ultricies cras adipiscing consectetur velit leo.'
        ),
        image: imagePresetStyle,
        heading2: heading2PresetStyle,
        heading2Content = i18n.gettext('Lorem ipsum dolor sit amet consectetur.'),
        paragraph2: paragraph2PresetStyle,
        paragraph2Content = i18n.gettext(
            'Lorem ipsum dolor sit amet consectetur. Sit sed vitae netus facilisi habitasse odio. Arcu in viverra sit velit. Posuere diam ultricies cras adipiscing consectetur velit leo.'
        ),
        image2: image2PresetStyle,
        heading3: heading3PresetStyle,
        heading3Content = i18n.gettext('Lorem ipsum dolor sit amet consectetur.'),
        paragraph3: paragraph3PresetStyle,
        paragraph3Content = i18n.gettext(
            'Lorem ipsum dolor sit amet consectetur. Sit sed vitae netus facilisi habitasse odio. Arcu in viverra sit velit. Posuere diam ultricies cras adipiscing consectetur velit leo.'
        ),
        image3: image3PresetStyle,
    } = presetStyle;

    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(0, 0, 0, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '12px',
        ...headingPresetStyle,
    });

    const paragraph = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        paragraphContent,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '12px',
            ...paragraphPresetStyle,
        }
    );

    const img1 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/topic-introduction-card-no-border-1_v1.png',
        ...imagePresetStyle,
    });

    const heading2 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        heading2Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(0, 0, 0, 1)',
            [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '12px',
            ...heading2PresetStyle,
        }
    );

    const paragraph2 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        paragraph2Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '12px',
            ...paragraph2PresetStyle,
        }
    );

    const img2 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/topic-introduction-card-no-border-2_v1.png',
        ...image2PresetStyle,
    });

    const heading3 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        heading3Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(0, 0, 0, 1)',
            [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '12px',
            ...heading3PresetStyle,
        }
    );

    const paragraph3 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        paragraph3Content,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '12px',
            ...paragraph3PresetStyle,
        }
    );

    const img3 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/topic-introduction-card-no-border-3_v1.png',
        ...image3PresetStyle,
    });

    return combineComponent(
        i18n.gettext('主題介紹卡片'),
        [
            [img1, heading, paragraph],
            [img2, heading2, paragraph2],
            [img3, heading3, paragraph3],
        ],
        {
            primaryGridType: GRID_LAYOUT_TYPE.SPLIT_H_3C,
            secondaryGridType: [
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SINGLE_CELL,
            ],
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.GRID_GAP]: '24px',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
                [FEATURE_FLAG_KEYS.PADDING_TOP]: '60px',
                [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '60px',
            },
            secondaryGridPresetStyle: [
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '12px',
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '12px',
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '12px',
                },
            ],
            shouldWrap: true,
        }
    );
}

export function createIntroductionCardWithBorder(
    content: string = officialSectionExhibitList[10].content
) {
    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(69, 69, 74, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '20px',
    });

    const paragraph = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        i18n.gettext(
            'Lorem ipsum dolor sit amet consectetur. Sit sed vitae netus facilisi habitasse odio. Arcu in viverra sit velit. Posuere diam ultricies cras adipiscing consectetur velit leo.'
        ),
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '20px',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '20px',
        }
    );

    const img = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/topic-introduction-card-with-border-1_v1.png',
    });

    const button = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK,
        i18n.gettext('Button'),
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '40px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '12px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'right',
        }
    );

    const heading2 = createRichText(OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(69, 69, 74, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '20px',
    });

    const paragraph2 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        i18n.gettext(
            'Lorem ipsum dolor sit amet consectetur. Sit sed vitae netus facilisi habitasse odio. Arcu in viverra sit velit. Posuere diam ultricies cras adipiscing consectetur velit leo.'
        ),
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '20px',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '20px',
        }
    );

    const img2 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/topic-introduction-card-with-border-2_v1.png',
    });

    const button2 = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK,
        i18n.gettext('Button'),
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '40px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '12px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'right',
        }
    );

    const heading3 = createRichText(OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(69, 69, 74, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '20px',
    });

    const paragraph3 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        i18n.gettext(
            'Lorem ipsum dolor sit amet consectetur. Sit sed vitae netus facilisi habitasse odio. Arcu in viverra sit velit. Posuere diam ultricies cras adipiscing consectetur velit leo.'
        ),
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '20px',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '20px',
        }
    );

    const img3 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/topic-introduction-card-with-border-3_v1.png',
    });

    const button3 = createButton(
        OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK,
        i18n.gettext('Button'),
        {
            [FEATURE_FLAG_KEYS.PADDING_X]: '40px',
            [FEATURE_FLAG_KEYS.PADDING_Y]: '12px',
            [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'right',
        }
    );

    return combineComponent(
        i18n.gettext('主題介紹卡片'),
        [
            [img, heading, paragraph, button],
            [img2, heading2, paragraph2, button2],
            [img3, heading3, paragraph3, button3],
        ],
        {
            primaryGridType: GRID_LAYOUT_TYPE.SPLIT_H_3C,
            secondaryGridType: [
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SINGLE_CELL,
            ],
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '506px',
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.GRID_GAP]: '24px',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
                [FEATURE_FLAG_KEYS.PADDING_TOP]: '64px',
                [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '64px',
            },
            secondaryGridPresetStyle: [
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                },
            ],
            shouldWrap: true,
        }
    );
}

export const officialSectionExhibitGenMap = {
    [OFFICIAL_SECTION_EXHIBIT_KEY.TEXT]: createIntroductionText,
    [OFFICIAL_SECTION_EXHIBIT_KEY.IMG]: createIntroductionImg,
    [OFFICIAL_SECTION_EXHIBIT_KEY.IMG_ARROW]: createIntroductionImgArrow,
    [OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_RIGHT]: createIntroductionTopicRight,
    [OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_LEFT]: createIntroductionTopicLeft,
    [OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_DOUBLE_COLUMN]:
        createIntroductionTopicDoubleColumn,
    [OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_DOUBLE_COLUMN_FULL_IMG]:
        createIntroductionTopicDoubleColumnFullImage,
    [OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_HIGHLIGHT]: createIntroductionHighlight,
    [OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_CARD_NO_BORDER]: createIntroductionCardNoBorder,
    [OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_CARD_WITH_BORDER]:
        createIntroductionCardWithBorder,
    [OFFICIAL_SECTION_EXHIBIT_KEY.TOPIC_BULLET_LIST]: createIntroductionBulletList,
};
